import axios from "axios";
import { GlobalState } from "context/GlobalContext";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { costingUrlKey } from "../CostingDetails/data";
import { Box, Button, ButtonGroup, Dialog, DialogActions, Divider, Stack, Typography } from "@mui/material";
import { datatypes, inputTypes, selectOptionsKeyName } from "data/globalData";
import { grey } from "@mui/material/colors";
import {
	cutQtyPrefilledColumn,
	generateAssocDataBasedOnMainDetailsChange,
	ocSplitQtyChannelSizeAssocObj,
	ocSplitQtyCutQtySplitAssocObj,
	splitQtyDummyData,
	splitQtySectionTypes,
	splitQtyTableColumns,
} from "./data";
import RenderInputField from "components/PimDashboard/CommonComponents/RenderInputField/RenderInputField";
import { constraintsOptions, formatColumnValue, getColDefaultValue, isColumnDefault } from "components/PimDashboard/data";
import { EditRounded } from "@mui/icons-material";
import ChannelSection from "./ChannelSection/ChannelSection";
import { split } from "lodash";
import { AuthState } from "context/AuthContext";
import { custom } from "data/authData";

const DisplayColumnData = ({ row, pageData, width = "24%", longWidth = "49%" }) => {
	return (
		<Box
			width={row.data_type === datatypes.LONGTEXT.key ? longWidth : width}
			key={row?.name}
			display="flex"
			border={`1px solid white`}
			boxSizing="border-box">
			<Typography
				variant="subtitle2"
				color={grey[200]}
				sx={{
					width: "38%",
					p: "10px 2%",
					background: "#000",
					overflow: "hidden",
					borderRadius: "1px 0px 0px 1px",
				}}>
				{row?.name}
			</Typography>
			<Typography
				variant="subtitle2"
				sx={{
					width: "55%",
					p: "10px 2%",
					background: grey[200],
					fontWeight: 400,
					overflow: "hidden",
					borderRadius: " 0px 1px 1px 0px",
				}}>
				{pageData?.[row?.key] !== undefined ? formatColumnValue(pageData?.[row?.key], row) : ""}
			</Typography>
		</Box>
	);
};

const inputWidth = "24%";
const inputLongWidth = "24%";

const SplitQty = ({ pageData, setPageData, selectedCostingDetails }) => {
	const { snackBar, setLoadingOpen, setSnackBarOpen, setSnackBar } = GlobalState();
	const { user } = AuthState();

	const disableEdit = user?.role?.[custom.COSTING_VIEWER.key] === true;

	let [editMode, setEditMode] = useState("");
	let [pageDataCopy, setPageDataCopy] = useState(null);

	let [mainDetailsEditModal, setMainDetailsEditModal] = useState(false);

	let [newSplitQty, setNewSplitQty] = useState(false);

	let [searchParams, setSearchParams] = useSearchParams();

	const defpageData = {
		...Object.values(splitQtyTableColumns)?.reduce((acc, row) => {
			return { ...acc, [row.key]: row.default_value || getColDefaultValue(row) };
		}, {}),
		[ocSplitQtyChannelSizeAssocObj]: [],
		[ocSplitQtyCutQtySplitAssocObj]: [],
	};

	useEffect(() => {
		const fetch = async () => {
			setLoadingOpen(true);
			setPageData();
			try {
				const res = await axios.get(`/api/sourcing/split_qty_full_data/${id}`);
				const result = res.data.result;
				if (result)
					setPageData(() => {
						let obj = { ...result };
						// delete obj[openCostingChannelWhSplitAssockey];
						// delete obj[openCostingQtySizeSplitAssockey];
						// return splitQtyDummyData;
						return obj;
					});
				else {
					setNewSplitQty(true);
					setPageData({ ...defpageData, [splitQtyTableColumns.style_ref_no.key]: id });
				}

				setLoadingOpen(false);
			} catch (error) {
				setSnackBarOpen(true);
				setSnackBar({ ...snackBar, message: parseErrorMessage(error) });
				setLoadingOpen(false);
			}
		};
		let id = searchParams.get(costingUrlKey);
		if (id && id !== pageData?.style_ref_no) fetch();
		else setPageData(null);
	}, [searchParams]);

	const handleEditModeChange = (sectionKey) => {
		if (editMode === "") {
			setEditMode(sectionKey);
			setPageDataCopy({ ...pageData });
		} else setEditMode("");
	};

	// main details edit modal
	const handleMainDetailsEditClick = () => {
		setMainDetailsEditModal(true);
		setPageDataCopy({ ...pageData });
	};
	const handleMainDetailsEditCancel = () => {
		setMainDetailsEditModal(false);
		setPageDataCopy({ ...pageData });
	};
	const handleMainDetailsSave = async (e) => {
		e.preventDefault();
		setLoadingOpen(true);

		const pendingFilelds = [];

		Object.values(splitQtyTableColumns).forEach((row) => {
			if (isColumnDefault(row, pageDataCopy[row.key]) && row?.constraints[constraintsOptions.NOTNULL.key]) {
				pendingFilelds.push(row);
			}
		});
		if (pendingFilelds.length > 0) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: `Please Enter the values for ${pendingFilelds.map((row) => row.name).join(",")}`,
				severity: "warning",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setLoadingOpen(false);
			return;
		}
		const changedAssocData = generateAssocDataBasedOnMainDetailsChange(pageDataCopy);
		try {
			const res = await axios.put(`/api/sourcing/update_split_qty_main_details/${selectedCostingDetails?.style_ref_no}`, {
				...pageDataCopy,
				...changedAssocData,
			});
			if (res) {
				setPageData(res.data.result);
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: "Data Saved Successfully",
					severity: "success",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
				setLoadingOpen(false);
				setMainDetailsEditModal(false);
				setNewSplitQty(false);
			}
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrorMessage(error),
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setLoadingOpen(false);
		}
	};
	const handleTableSaveCallback = (data) => {
		setPageData((prev) => {
			return { ...prev, ...data };
		});
		setPageDataCopy();
		setEditMode("");
	};

	if (!pageData) return <div>Loading...</div>;
	return (
		<Stack pt={2} spacing={2}>
			{/* main Details */}
			<Box display="flex" width="99%" mb={2} flexWrap={"wrap"} columnGap="1%">
				<Box width="100%" display="flex" justifyContent="space-between" alignItems="center" mb={2}>
					<Typography variant="h6">Main Details</Typography>
					<Button variant="contained" size="small" onClick={handleMainDetailsEditClick} startIcon={<EditRounded />}>
						Edit
					</Button>
				</Box>
				{Object.values(splitQtyTableColumns)?.map((row) => {
					return <DisplayColumnData row={row} pageData={pageData} width={inputWidth} longWidth={inputLongWidth} />;
				})}
			</Box>
			<Divider sx={{ width: "100%" }} />

			{!newSplitQty && (
				<ChannelSection
					editMode={editMode === splitQtySectionTypes.order_qty_input.key}
					disableEdit={disableEdit}
					setEditMode={handleEditModeChange}
					handleSaveCallback={handleTableSaveCallback}
					pageData={pageData}
					setPageData={setPageData}
					selectedCostingDetails={selectedCostingDetails}
				/>
			)}

			<Dialog
				fullWidth
				maxWidth="lg"
				open={mainDetailsEditModal}
				onClose={handleMainDetailsEditCancel}
				component="form"
				onSubmit={handleMainDetailsSave}>
				<Box width="98%" p={2} boxSizing="border-box">
					<Stack width="100%" justifyContent="space-between" flexWrap="wrap" rowGap={1}>
						<Stack direction="row" width="100%" flexWrap="wrap" columnGap="1%" rowGap={1}>
							<Typography variant="h6" width="100%" pl={1}>
								Edit Main Details
							</Typography>
							{Object.values(splitQtyTableColumns).map((row) => (
								<RenderInputField
									column={row}
									data={pageDataCopy}
									setData={setPageDataCopy}
									selectOptObjName={selectOptionsKeyName}
									width={"38%"}
									longWidth={"48%"}
								/>
							))}
						</Stack>
					</Stack>
				</Box>
				<DialogActions>
					<Button variant="outlined" onClick={handleMainDetailsEditCancel}>
						Cancel
					</Button>
					<Button autoFocus variant="contained" sx={{ mr: 1 }} type="submit">
						Save
					</Button>
				</DialogActions>
			</Dialog>
		</Stack>
	);
};

export default SplitQty;
